/**
 * PROC-000054 item 2: the ONE parsed-requirement formatter/renderer, shared
 * by ProcurementDetail's "Parsed Requirement" card and the wizard's Answers/
 * Questions-step context card (RequirementContextCard) - single-source
 * presentation (Principle 10), not a second copy that can drift from the
 * detail page's. Extracted verbatim from ProcurementDetail.jsx's prior
 * inline IIFE; ProcurementDetail now renders through this too.
 */
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { procurementTypeLabel } from '@/lib/procurementTypeLabel';
import { readCategoryNoFit } from '@/lib/categoryNoFit';

/** {custom_code: category_name} lookup, fetched once - used to render
 * "category" as "Category Name (CODE)" instead of the bare code. */
export const useCategoryLookup = () => {
  const [categoryLookup, setCategoryLookup] = useState({});

  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        const catResponse = await api.getMasterCategories();
        if (catResponse.data && isMounted) {
          const lookup = {};
          catResponse.data.forEach(cat => {
            lookup[cat.custom_code] = cat.category_name;
          });
          setCategoryLookup(lookup);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
    return () => { isMounted = false; };
  }, []);

  return categoryLookup;
};

/** {key, label, value} for every displayable field in a parsed_requirement
 * object - handles the nested {parsed_requirement: {...}} shape some
 * payloads carry, formats arrays/objects/category the same way regardless
 * of caller. Returns [] for a missing/malformed parsedRequirement (never
 * throws) so callers can render nothing rather than an empty grid. */
export const formatParsedRequirementFields = (parsedRequirement, categoryLookup = {}) => {
  let parsedData = parsedRequirement;
  if (parsedData?.parsed_requirement && typeof parsedData.parsed_requirement === 'object') {
    parsedData = parsedData.parsed_requirement;
  }
  if (!parsedData || typeof parsedData !== 'object') return [];

  // Filter out internal/technical keys. category_confidence (zero-floor
  // category guard, PROC-000026 family) drives a DIFFERENT UI for the
  // category field itself (see getCategoryConfidence below) - never its own
  // passive grid row. category_proposal/category_proposal_source/category_
  // proposal_confidence/category_proposal_margin/category_decision_source
  // (RECOMMENDATION VS DECISION, 2026-08-02, sharpened 2026-08-03 - memory/
  // DATAFLOW.md's Principle 14; supersedes the retired category_source) are
  // audit-trail fields, not buyer-facing display fields - same treatment.
  // No category_final entry: that field was removed 2026-08-03 (`category`
  // is the one canonical final value) - never existed long enough to need
  // an exclusion here in production.
  // location_* (Phase 2 slice 2, 2026-08-06) get the SAME treatment as
  // category_* above, and for the same reason: they are an audit trail,
  // not buyer-facing display fields. Added in slice 3 as a FIX, not a
  // precaution - slice 2 landed the six fields without excluding them,
  // so this generic grid (rendered on ProcurementDetail) was already
  // showing rows like "Location Proposal Source: deterministic_matcher"
  // and "Location Decision Source: pending_review" to buyers. Slice 2's
  // own commit message claimed no UI change; it was wrong about this
  // one file. The proposal reaches the buyer deliberately, in one place
  // only: as the pre-filled ship-to answer and its provenance line (see
  // getLocationProposal below and LocationDropdown).
  const excludeKeys = [
    'parsed_requirement',
    'category_confidence',
    'category_proposal',
    'category_proposal_source',
    'category_proposal_confidence',
    'category_proposal_margin',
    'category_decision_source',
    'location_proposal',
    'location_proposal_source',
    'location_proposal_confidence',
    'location_proposal_margin',
    'location_proposal_matched_on',
    'location_decision_source',
  ];

  return Object.entries(parsedData)
    .filter(([key]) => !excludeKeys.includes(key))
    .map(([key, value]) => {
      let displayValue = 'N/A';
      if (value !== null && value !== undefined && value !== '') {
        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            displayValue = value.join(', ') || 'N/A';
          } else {
            displayValue = Object.entries(value)
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ') || 'N/A';
          }
        } else {
          displayValue = String(value);
          if (key === 'category' && categoryLookup[value]) {
            displayValue = `${categoryLookup[value]} (${value})`;
          } else if (key === 'procurement_type') {
            // TYPE LABEL PARITY PATCH (2026-07-28 ruling): shared with
            // document_generator.py's Executive Summary label - never
            // render the raw enum ("mixed") here.
            displayValue = procurementTypeLabel(value);
          }
        }
      }
      return {
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: displayValue,
      };
    });
};

/** Raw category_confidence value ("none", or undefined/null when signal was
 * present) from a parsed_requirement payload - handles the same nested
 * {parsed_requirement: {...}} shape as formatParsedRequirementFields above.
 * For gating the forced category-selection UI, never for display (that's
 * every other field's job via the formatter above). */
export const getCategoryConfidence = (parsedRequirement) => {
  let parsedData = parsedRequirement;
  if (parsedData?.parsed_requirement && typeof parsedData.parsed_requirement === 'object') {
    parsedData = parsedData.parsed_requirement;
  }
  return (parsedData && typeof parsedData === 'object') ? (parsedData.category_confidence ?? null) : null;
};

/** The HEADER no-fit record (F1 / C1b-1b), or null. Same nested-shape
 * handling as its siblings here, which is exactly why it lives beside them
 * rather than at the call site: the {parsed_requirement: {...}} unwrap is
 * one rule and this file is where it is written.
 *
 * The record itself is judged by THE shared reader (lib/categoryNoFit.js,
 * the JS half of a registered mirror pair) - this helper only finds the
 * container to hand it. So this surface, the line picker, the save-arm
 * exemption and the admin query all agree about what counts. */
export const getCategoryNoFit = (parsedRequirement) => {
  let parsedData = parsedRequirement;
  if (parsedData?.parsed_requirement && typeof parsedData.parsed_requirement === 'object') {
    parsedData = parsedData.parsed_requirement;
  }
  return readCategoryNoFit(parsedData);
};

/** The container the no-fit record hangs on, unwrapped - what the picker
 * needs as its `noFitSubject`. Separate from the reader above because the
 * picker must be handed the SUBJECT (so it can read the record itself and
 * its request number), not the record. */
export const getCategoryNoFitSubject = (parsedRequirement) => {
  let parsedData = parsedRequirement;
  if (parsedData?.parsed_requirement && typeof parsedData.parsed_requirement === 'object') {
    parsedData = parsedData.parsed_requirement;
  }
  return (parsedData && typeof parsedData === 'object') ? parsedData : null;
};

/** The location proposal block (Phase 2 slice 3, 2026-08-06), or null.
 * Same nested-shape handling as the helpers around it.
 *
 * Returns {code, confidence} and nothing else - the two facts the
 * ship-to pre-fill needs. margin/matched_on/source are audit fields with
 * no UI consumer; they are deliberately not surfaced here, so a future
 * reader can see at a glance exactly how much of the block the interface
 * depends on.
 *
 * Returns null for a legacy document that predates the block. Callers
 * must treat null and confidence "none" identically: no pre-fill. */
export const getLocationProposal = (parsedRequirement) => {
  let parsedData = parsedRequirement;
  if (parsedData?.parsed_requirement && typeof parsedData.parsed_requirement === 'object') {
    parsedData = parsedData.parsed_requirement;
  }
  if (!parsedData || typeof parsedData !== 'object') return null;
  if (!parsedData.location_proposal_confidence) return null;
  return {
    code: parsedData.location_proposal ?? null,
    confidence: parsedData.location_proposal_confidence,
  };
};

/** Raw category CODE (e.g. "601090") from a parsed_requirement payload, same
 * nested-shape handling - used to look up the LLM's own (unconfirmed) pick
 * to label alongside the forced-selection dropdown. */
export const getRawCategoryCode = (parsedRequirement) => {
  let parsedData = parsedRequirement;
  if (parsedData?.parsed_requirement && typeof parsedData.parsed_requirement === 'object') {
    parsedData = parsedData.parsed_requirement;
  }
  return (parsedData && typeof parsedData === 'object') ? (parsedData.category ?? null) : null;
};

/** The grid itself - renders nothing (null) when there are no fields to
 * show, so callers can drop it in unconditionally. */
export const ParsedRequirementGrid = ({ parsedRequirement, categoryLookup = {} }) => {
  const fields = formatParsedRequirementFields(parsedRequirement, categoryLookup);
  if (fields.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      {fields.map(({ key, label, value }) => (
        <div key={key} className="bg-zinc-50 p-3 rounded">
          <div className="text-xs text-zinc-500 mb-1">{label}</div>
          <div className="text-sm text-zinc-900 font-medium break-words">{value}</div>
        </div>
      ))}
    </div>
  );
};
